import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import {
  Plus, ArrowLeft, Trash2, Pencil, Loader2, Sparkles, MapPin, Calendar as CalendarIcon,
  IndianRupee, Users, ChevronRight, AlertTriangle, CheckCircle, Save,
  UserPlus, XCircle, X, ChevronDown, Image as ImageIcon, Upload, Download, Filter,
  Eye, EyeOff, RotateCw, Lock, Globe, Link as LinkIcon, Check, Tag, Percent, Send,
  ArrowRightLeft,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_TYPES = ['tournament', 'fitness', 'screening', 'networking', 'other'];
const SPORTS = ['football', 'padel', 'pickleball', 'badminton', 'cricket', 'none'];
const FORMATS = ['individual', 'team', 'ticket', 'free'];

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.includes('T') ? s : s + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtTime = (t) => t || '';
const statusPill = (s) => {
  const map = {
    draft: 'bg-gray-100 text-gray-700',
    open: 'bg-emerald-100 text-emerald-700',
    full: 'bg-amber-100 text-amber-700',
    ongoing: 'bg-blue-100 text-blue-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return map[s] || 'bg-gray-100 text-gray-700';
};

// ============== LIST ==============
const EventsList = ({ events, loading, onCreate, onOpen }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const filtered = statusFilter === 'all' ? events : events.filter(e => e.status === statusFilter);

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6" data-testid="events-listing">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500 mt-1">Tournaments, fitness sessions, screenings & multi-activity events</p>
        </div>
        <Button onClick={onCreate} className="gap-2" data-testid="create-event-btn">
          <Plus className="h-4 w-4" /> Create Event
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="events-status-filter">
        {['all', 'draft', 'open', 'full', 'ongoing', 'completed', 'cancelled'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
              statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`events-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border rounded-2xl p-12 text-center">
          <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No events yet</p>
          <p className="text-xs text-gray-400 mt-1">Create your first event to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(ev => (
            <button
              key={ev.id}
              onClick={() => onOpen(ev.id)}
              className="text-left bg-white border rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`event-card-${ev.slug || ev.id}`}
            >
              <div className="aspect-video bg-gray-100 relative">
                {ev.banner_image_url ? (
                  <img src={`${API_URL}${ev.banner_image_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <ImageIcon className="h-12 w-12" />
                  </div>
                )}
                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusPill(ev.status)}`}>
                  {ev.status}
                </span>
                {ev.is_visible === false && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-gray-900/80 text-white flex items-center gap-1" data-testid={`event-hidden-badge-${ev.id}`}>
                    <EyeOff className="h-2.5 w-2.5" /> Hidden
                  </span>
                )}
                {ev.is_visible !== false && ev.is_public === false && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-purple-600/90 text-white flex items-center gap-1" data-testid={`event-private-badge-${ev.id}`}>
                    <Lock className="h-2.5 w-2.5" /> Private
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm text-gray-900 line-clamp-1">{ev.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{ev.venue_name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {fmtDate(ev.start_date)}{ev.start_date !== ev.end_date ? ` – ${fmtDate(ev.end_date)}` : ''}
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400 text-[10px] uppercase">Activities</p>
                    <p className="font-semibold">{ev.activity_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-[10px] uppercase">Registrations</p>
                    <p className="font-semibold">{ev.total_registrations || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-[10px] uppercase">Revenue</p>
                    <p className="font-semibold flex items-center"><IndianRupee className="h-3 w-3" />{(ev.total_revenue || 0).toFixed(0)}</p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== CREATE WIZARD ==============
const blankActivity = (date) => ({
  title: '',
  description: '',
  activity_type: 'tournament',
  sport: 'football',
  format: 'individual',
  team_size: 7,
  date: date || '',
  start_time: '16:00',
  end_time: '18:00',
  max_participants: 16,
  price_per_unit: 0,
  registration_deadline: '',
});

const CreateEventWizard = ({ onCancel, onCreated }) => {
  const { user, isSuperAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [bannerUploading, setBannerUploading] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    venue_id: user?.facility_id || '',
    banner_image_url: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    is_multi_activity: false,
    is_public: true,
    activities: [blankActivity('')],
    publish: false,
  });

  useEffect(() => {
    if (isSuperAdmin) {
      api.request('/facilities').then(d => setFacilities(d || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Auto-sync activity dates with event start date for single-activity
  useEffect(() => {
    if (!form.is_multi_activity && form.start_date) {
      setForm(p => ({
        ...p,
        activities: p.activities.map(a => ({ ...a, date: a.date || p.start_date })),
      }));
    }
  }, [form.start_date, form.is_multi_activity]);

  const updateActivity = (idx, patch) => {
    setForm(p => ({ ...p, activities: p.activities.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  };
  const addActivity = () => setForm(p => ({ ...p, activities: [...p.activities, blankActivity(p.start_date)] }));
  const removeActivity = (idx) => setForm(p => ({ ...p, activities: p.activities.filter((_, i) => i !== idx) }));

  const handleBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/events/upload-banner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { url } = await res.json();
      setForm(p => ({ ...p, banner_image_url: url }));
      toast({ title: 'Banner uploaded', description: 'Preview is below.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBannerUploading(false);
    }
  };

  const canNextStep1 = () =>
    form.title.trim() && form.venue_id && form.start_date && form.end_date && form.registration_deadline;
  const canNextStep2 = () => {
    if (!form.is_multi_activity && form.activities.length !== 1) return false;
    return form.activities.every(a =>
      a.title.trim() && a.activity_type && a.format && a.date && a.start_time && a.end_time
      && a.max_participants > 0
      && (a.format !== 'free' || (a.price_per_unit === 0 || a.price_per_unit === '0' || a.price_per_unit === ''))
      && (a.format !== 'team' || (a.team_size > 0))
    );
  };

  const save = async (publish) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        publish,
        activities: form.activities.map(a => ({
          ...a,
          max_participants: Number(a.max_participants),
          price_per_unit: Number(a.price_per_unit) || 0,
          team_size: a.format === 'team' ? Number(a.team_size) : null,
          registration_deadline: a.registration_deadline || form.registration_deadline,
        })),
      };
      const res = await api.request('/events', { method: 'POST', body: JSON.stringify(payload) });
      toast({ title: publish ? 'Event Published' : 'Draft Saved', description: `Created "${form.title}"` });
      onCreated(res.event.id);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-3xl mx-auto" data-testid="create-event-wizard">
      <button onClick={onCancel} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-events">
        <ArrowLeft className="h-4 w-4" /> Back to Events
      </button>

      <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Create Event</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs">
        {[1, 2, 3].map(n => (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${step === n ? 'bg-gray-900 text-white' : step > n ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              <span className="font-semibold">Step {n}</span>
              <span>{n === 1 ? 'Basics' : n === 2 ? 'Activities' : 'Review'}</span>
            </div>
            {n < 3 && <ChevronRight className="h-4 w-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-1">
          <div>
            <label className="text-xs font-semibold text-gray-700">Event title *</label>
            <input
              type="text" value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Powerplay 2027"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="event-title-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="What's the event about?"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="event-description-input"
            />
          </div>

          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold text-gray-700">Venue *</label>
              <select
                value={form.venue_id}
                onChange={e => setForm(p => ({ ...p, venue_id: e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="event-venue-select"
              >
                <option value="">Select a venue</option>
                {facilities.filter(f => f.status !== 'inactive').map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Banner */}
          <div>
            <label className="text-xs font-semibold text-gray-700">Banner image (16:9 recommended)</label>
            {form.banner_image_url ? (
              <div className="mt-2 relative rounded-xl overflow-hidden border" data-testid="banner-preview">
                <div className="aspect-video bg-gray-100">
                  <img src={`${API_URL}${form.banner_image_url}`} alt="banner" className="w-full h-full object-cover" />
                </div>
                <button
                  onClick={() => setForm(p => ({ ...p, banner_image_url: '' }))}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-md hover:bg-white"
                  data-testid="banner-remove-btn"
                ><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="mt-2 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-xl py-8 cursor-pointer hover:border-gray-400" data-testid="banner-upload-label">
                {bannerUploading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : <Upload className="h-5 w-5 text-gray-400" />}
                <span className="text-xs text-gray-500">{bannerUploading ? 'Uploading…' : 'Click to upload JPG/PNG (max 8MB)'}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleBanner} data-testid="banner-file-input" />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Start date *</label>
              <input type="date" value={form.start_date}
                onChange={e => setForm(p => ({ ...p, start_date: e.target.value, end_date: p.end_date || e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="event-start-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">End date *</label>
              <input type="date" value={form.end_date}
                onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                min={form.start_date}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="event-end-date" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Registration deadline *</label>
            <input type="datetime-local" value={form.registration_deadline}
              onChange={e => setForm(p => ({ ...p, registration_deadline: e.target.value }))}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="event-reg-deadline" />
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
            <div>
              <p className="text-sm font-semibold">Multi-Activity Event</p>
              <p className="text-xs text-gray-500">Toggle on for Powerplay-style events with multiple activities</p>
            </div>
            <button
              onClick={() => setForm(p => ({
                ...p,
                is_multi_activity: !p.is_multi_activity,
                activities: !p.is_multi_activity ? p.activities : p.activities.slice(0, 1),
              }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.is_multi_activity ? 'bg-gray-900' : 'bg-gray-300'}`}
              data-testid="multi-activity-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform ${form.is_multi_activity ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
            <div className="pr-3">
              <p className="text-sm font-semibold">{form.is_public ? 'Public event' : 'Private event (invite only)'}</p>
              <p className="text-xs text-gray-500">Private events won't appear on the public events listing but are accessible via direct link.</p>
            </div>
            <button
              onClick={() => setForm(p => ({ ...p, is_public: !p.is_public }))}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.is_public ? 'bg-gray-900' : 'bg-gray-300'}`}
              data-testid="is-public-toggle"
              aria-pressed={form.is_public}
            >
              <span className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform ${form.is_public ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!canNextStep1()} data-testid="step1-next-btn">
              Next: Activities
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="step-2">
          {form.activities.map((a, idx) => (
            <ActivityEditor
              key={idx}
              idx={idx}
              activity={a}
              canRemove={form.is_multi_activity && form.activities.length > 1}
              onChange={(patch) => updateActivity(idx, patch)}
              onRemove={() => removeActivity(idx)}
              eventStart={form.start_date}
              eventEnd={form.end_date}
              isSingleActivity={!form.is_multi_activity}
            />
          ))}
          {form.is_multi_activity && (
            <button
              onClick={addActivity}
              className="w-full border-2 border-dashed border-gray-300 rounded-xl py-4 text-sm text-gray-600 hover:border-gray-400 flex items-center justify-center gap-2"
              data-testid="add-activity-btn"
            >
              <Plus className="h-4 w-4" /> Add another activity
            </button>
          )}
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={() => setStep(3)} disabled={!canNextStep2()} data-testid="step2-next-btn">
              Next: Review
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="step-3">
          <h2 className="font-semibold">Review</h2>
          <div className="bg-gray-50 rounded-xl p-4 space-y-1 text-sm">
            <p><span className="text-gray-500">Title:</span> <strong>{form.title}</strong></p>
            <p><span className="text-gray-500">Dates:</span> {fmtDate(form.start_date)} – {fmtDate(form.end_date)}</p>
            <p><span className="text-gray-500">Type:</span> {form.is_multi_activity ? 'Multi-Activity' : 'Single Activity'}</p>
            <p><span className="text-gray-500">Activities:</span> {form.activities.length}</p>
          </div>
          {form.activities.map((a, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-3 text-sm">
              <p className="font-medium">{a.title}</p>
              <p className="text-xs text-gray-500">
                {a.activity_type} · {a.format} · {a.sport !== 'none' ? a.sport + ' · ' : ''}
                {a.date} {a.start_time}–{a.end_time}
              </p>
              <p className="text-xs text-gray-500">Max {a.max_participants}{a.format === 'team' ? ' teams' : ''} · {a.price_per_unit > 0 ? `₹${a.price_per_unit}${a.format === 'team' ? '/team' : ''}` : 'Free'}</p>
            </div>
          ))}
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">
                Save Draft
              </Button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="publish-event-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActivityEditor = ({ idx, activity, canRemove, onChange, onRemove, eventStart, eventEnd, isSingleActivity }) => {
  const free = activity.format === 'free';
  const team = activity.format === 'team';
  const maxLabel = team ? 'Maximum Teams' : 'Maximum Participants';
  const priceLabel = team ? 'Price per Team (\u20B9)' : 'Price per Person (\u20B9)';
  return (
    <div className="bg-white border rounded-2xl p-4 space-y-4" data-testid={`activity-editor-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Activity {idx + 1}</p>
        {canRemove && (
          <button onClick={onRemove} className="text-red-500 hover:bg-red-50 rounded-md p-1.5" data-testid={`remove-activity-${idx}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Activity Name */}
      <div>
        <label className="text-xs font-semibold text-gray-700">Activity Name</label>
        <input type="text" value={activity.title}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="e.g. Football Tournament 7v7"
          className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
          data-testid={`activity-${idx}-title`} />
      </div>

      {/* Description (optional) */}
      <div>
        <label className="text-xs font-semibold text-gray-700">Description (optional)</label>
        <textarea value={activity.description || ''}
          onChange={e => onChange({ description: e.target.value })}
          placeholder="Briefly describe this activity — rules, prizes, what to bring."
          rows={3}
          className="mt-1 w-full border rounded-lg px-3 py-2 text-sm resize-y"
          data-testid={`activity-${idx}-description`} />
      </div>

      {/* Activity Type + Sport */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-700">Activity Type</label>
          <select value={activity.activity_type} onChange={e => onChange({ activity_type: e.target.value })}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize" data-testid={`activity-${idx}-type`}>
            {ACTIVITY_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700">Sport</label>
          <select value={activity.sport} onChange={e => onChange({ sport: e.target.value })}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize" data-testid={`activity-${idx}-sport`}>
            {SPORTS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
        </div>
      </div>

      {/* Registration Format + Players Per Team (only when team) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-700">Registration Format</label>
          <select value={activity.format}
            onChange={e => {
              const f = e.target.value;
              onChange({ format: f, ...(f === 'free' ? { price_per_unit: 0 } : {}) });
            }}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize" data-testid={`activity-${idx}-format`}>
            {FORMATS.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
          </select>
        </div>
        {team && (
          <div>
            <label className="text-xs font-semibold text-gray-700">Players Per Team</label>
            <input type="number" min="1" value={activity.team_size}
              onChange={e => onChange({ team_size: Number(e.target.value) })}
              placeholder="e.g. 7"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid={`activity-${idx}-team-size`} />
            <p className="text-[10px] text-gray-500 mt-1">Number of players each team must register (e.g. 7 for 7-a-side football)</p>
          </div>
        )}
      </div>

      {/* Maximum Teams / Maximum Participants */}
      <div>
        <label className="text-xs font-semibold text-gray-700">{maxLabel}</label>
        <input type="number" min="1" value={activity.max_participants}
          onChange={e => onChange({ max_participants: Number(e.target.value) })}
          placeholder={team ? 'e.g. 8' : 'e.g. 20'}
          className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
          data-testid={team ? `activity-${idx}-max-teams` : `activity-${idx}-max`} />
      </div>

      {/* Date + Times */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-700">Activity Date</label>
          <input type="date" value={activity.date}
            min={eventStart} max={eventEnd}
            onChange={e => onChange({ date: e.target.value })}
            disabled={isSingleActivity}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
            data-testid={`activity-${idx}-date`} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700">Start Time</label>
          <input type="time" value={activity.start_time}
            onChange={e => onChange({ start_time: e.target.value })}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid={`activity-${idx}-start`} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700">End Time</label>
          <input type="time" value={activity.end_time}
            onChange={e => onChange({ end_time: e.target.value })}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid={`activity-${idx}-end`} />
        </div>
      </div>

      {/* Price — hidden entirely when format='free' */}
      {!free && (
        <div>
          <label className="text-xs font-semibold text-gray-700">{priceLabel}</label>
          <input type="number" min="0" value={activity.price_per_unit}
            onChange={e => onChange({ price_per_unit: Number(e.target.value) })}
            placeholder="0"
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            data-testid={`activity-${idx}-price`} />
          <p className="text-[10px] text-gray-500 mt-1">Enter 0 for free activities</p>
        </div>
      )}
    </div>
  );
};

// ============== DETAIL DASHBOARD ==============
const EventDetail = ({ eventId, onBack }) => {
  const { user } = useAuth();
  const canReschedule = ['admin', 'superadmin'].includes(user?.role);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityFilter, setActivityFilter] = useState('all');
  const [expandedReg, setExpandedReg] = useState(null);
  const [showAdd, setShowAdd] = useState(null); // activity id
  const [cancelTarget, setCancelTarget] = useState(null); // {registration, activity}
  const [changeCategoryTarget, setChangeCategoryTarget] = useState(null); // {registration, currentActivity}
  const [editTarget, setEditTarget] = useState(null); // activity object
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // activity object
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.request(`/events/${eventId}`);
      setEvent(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !event) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  const activities = event.activities || [];
  const allRegs = activities.flatMap(a => (a.registrations || []).map(r => ({ ...r, _activity: a })));
  const activeRegs = allRegs.filter(r => r.status === 'active' && ['paid', 'manual'].includes(r.payment_status));
  const totalRevenue = activeRegs.reduce((s, r) => s + (r.amount_paid || 0), 0);
  const filteredRegs = activityFilter === 'all' ? allRegs : allRegs.filter(r => r.activity_id === activityFilter);

  const handleCancelEvent = async () => {
    if (!window.confirm('Cancel this entire event? All active registrations will be refunded.')) return;
    try {
      const res = await api.request(`/events/${event.id}/cancel`, { method: 'POST' });
      toast({ title: 'Event cancelled', description: `${res.refunded} refunded, ${res.failed} failed.` });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Mark this event as completed?')) return;
    try {
      await api.request(`/events/${event.id}/complete`, { method: 'POST' });
      toast({ title: 'Marked completed' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handlePublish = async () => {
    try {
      await api.request(`/events/${event.id}/publish`, { method: 'POST' });
      toast({ title: 'Published' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleCancelActivity = async (act) => {
    if (!window.confirm(`Cancel activity "${act.title}"? All active registrations will be refunded.`)) return;
    try {
      const res = await api.request(`/events/${event.id}/activities/${act.id}/cancel`, { method: 'POST' });
      toast({ title: 'Activity cancelled', description: `${res.refunded} refunded, ${res.failed} failed.` });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleExportXlsx = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/events/${event.id}/export/registrations`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fileName = match ? match[1] : `${event.title}_registrations.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: fileName });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleToggleVisibility = async () => {
    setVisibilityBusy(true);
    try {
      const action = event.is_visible === false ? 'enable' : 'disable';
      await api.request(`/events/${event.id}/${action}`, { method: 'POST' });
      toast({ title: action === 'enable' ? 'Event enabled' : 'Event hidden from public' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setVisibilityBusy(false);
    }
  };

  const handleTogglePrivacy = async () => {
    try {
      const isPublic = event.is_public !== false;
      const action = isPublic ? 'make-private' : 'make-public';
      await api.request(`/events/${event.id}/${action}`, { method: 'POST' });
      toast({ title: isPublic ? 'Event is now private' : 'Event is now public' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleReactivate = async () => {
    if (!window.confirm(
      'This will make the event live again. Previously refunded registrations will not be automatically restored — customers will need to register again. Continue?'
    )) return;
    try {
      await api.request(`/events/${event.id}/reactivate`, { method: 'POST' });
      toast({ title: 'Event reactivated', description: 'All activities flipped back to open.' });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleCopyLink = async () => {
    // Route through /api/share/events/{slug} so social-media crawlers
    // (WhatsApp, FB, Twitter, LinkedIn, Telegram) receive per-event OG tags
    // in the initial HTML — humans get an instant client-side redirect to
    // the SPA at /events/{slug}. See backend/routes/share_previews.py.
    const url = `https://playspaces.in/api/share/events/${event.slug}`;
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    };
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        copied = true;
      } else {
        copied = fallback();
      }
    } catch (_e) {
      // Clipboard API rejected (e.g. headless / insecure context) — try DOM fallback
      try { copied = fallback(); } catch (_) { /* noop */ }
    }
    if (copied) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } else {
      toast({ title: 'Copy failed', description: url, variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 lg:p-6 pb-28 lg:pb-6 max-w-6xl mx-auto" data-testid="event-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-from-detail">
        <ArrowLeft className="h-4 w-4" /> Back to Events
      </button>

      {/* Header */}
      <div className="bg-white border rounded-2xl overflow-hidden mb-4">
        {event.banner_image_url && (
          <div className="aspect-[3/1] bg-gray-100">
            <img src={`${API_URL}${event.banner_image_url}`} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold">{event.title}</h1>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="h-3.5 w-3.5" /> {event.venue_name}
              </p>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                <CalendarIcon className="h-3.5 w-3.5" /> {fmtDate(event.start_date)}{event.start_date !== event.end_date ? ` – ${fmtDate(event.end_date)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusPill(event.status)}`}>{event.status}</span>
              {event.is_multi_activity && <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-700 uppercase">Multi-Activity</span>}
              {event.is_visible === false && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-200 text-gray-700 uppercase flex items-center gap-1" data-testid="event-hidden-badge">
                  <EyeOff className="h-3 w-3" /> Hidden
                </span>
              )}
              {event.is_public === false && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-purple-100 text-purple-700 uppercase flex items-center gap-1" data-testid="event-private-badge">
                  <Lock className="h-3 w-3" /> Private
                </span>
              )}
            </div>
          </div>

          {event.is_visible === false && event.status !== 'cancelled' && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5" data-testid="event-hidden-banner">
              <EyeOff className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">This event is hidden from the public listing. Customers cannot see or register for it until you enable it again.</p>
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {event.status === 'draft' && (
              <Button size="sm" onClick={handlePublish} data-testid="publish-btn"><CheckCircle className="h-3.5 w-3.5 mr-1" /> Publish</Button>
            )}
            {!['cancelled', 'completed'].includes(event.status) && (
              <>
                <Button size="sm" variant="outline" onClick={handleComplete} data-testid="complete-btn">Mark Completed</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleToggleVisibility}
                  disabled={visibilityBusy}
                  className={event.is_visible === false ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50' : ''}
                  data-testid="toggle-visibility-btn"
                >
                  {visibilityBusy ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : event.is_visible === false ? (
                    <Eye className="h-3.5 w-3.5 mr-1" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 mr-1" />
                  )}
                  {event.is_visible === false ? 'Enable Event' : 'Disable Event'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTogglePrivacy}
                  className={event.is_public === false ? 'text-purple-700 border-purple-200 hover:bg-purple-50' : ''}
                  data-testid="toggle-privacy-btn"
                >
                  {event.is_public === false ? (
                    <><Globe className="h-3.5 w-3.5 mr-1" /> Make Public</>
                  ) : (
                    <><Lock className="h-3.5 w-3.5 mr-1" /> Make Private</>
                  )}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditEventOpen(true)} data-testid="edit-event-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Event
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancelEvent} data-testid="cancel-event-btn">
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel Event
                </Button>
              </>
            )}
            {event.status === 'cancelled' && (
              <Button
                size="sm"
                variant="outline"
                className="text-blue-700 border-blue-200 hover:bg-blue-50"
                onClick={handleReactivate}
                data-testid="reactivate-event-btn"
              >
                <RotateCw className="h-3.5 w-3.5 mr-1" /> Reactivate Event
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportXlsx}
              disabled={exporting}
              data-testid="export-registrations-btn"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Export to Excel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyLink}
              className={linkCopied ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : ''}
              data-testid="copy-link-btn"
            >
              {linkCopied
                ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied!</>
                : <><LinkIcon className="h-3.5 w-3.5 mr-1" /> Copy Link</>}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Stat label="Activities" value={activities.length} />
            <Stat label="Registrations" value={activeRegs.length} />
            <Stat label="Revenue" value={`₹${totalRevenue.toFixed(0)}`} />
          </div>
        </div>
      </div>

      {/* Activity summary cards */}
      <h2 className="text-sm font-semibold text-gray-700 mb-2">Activities</h2>
      <div className="space-y-2 mb-6">
        {activities.map(a => (
          <ActivitySummaryCard
            key={a.id}
            activity={a}
            onCancel={() => handleCancelActivity(a)}
            onAddRegistration={() => setShowAdd(a)}
            onEdit={() => setEditTarget(a)}
            onReschedule={canReschedule ? () => setRescheduleTarget(a) : null}
            disabled={['cancelled', 'completed'].includes(event.status)}
          />
        ))}
      </div>

      {/* Coupons */}
      <CouponsSection eventId={event.id} activities={activities} />

      {/* Registrations list */}
      <div className="bg-white border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-semibold text-sm">Registrations</h2>
          <select
            value={activityFilter}
            onChange={e => setActivityFilter(e.target.value)}
            className="border rounded-lg px-2 py-1 text-xs bg-white"
            data-testid="reg-activity-filter"
          >
            <option value="all">All activities</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        </div>
        {filteredRegs.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No registrations yet</p>
        ) : (
          <div className="space-y-2" data-testid="registrations-list">
            {filteredRegs.map(r => (
              <RegistrationRow
                key={r.id}
                reg={r}
                activity={r._activity}
                expanded={expandedReg === r.id}
                onToggle={() => setExpandedReg(expandedReg === r.id ? null : r.id)}
                onCancel={() => setCancelTarget({ registration: r, activity: r._activity })}
                onChangeCategory={() => setChangeCategoryTarget({ registration: r, currentActivity: r._activity })}
              />
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AdminAddRegistrationModal
          activity={showAdd}
          eventId={event.id}
          onClose={() => setShowAdd(null)}
          onAdded={() => { setShowAdd(null); load(); }}
        />
      )}
      {cancelTarget && (
        <CancelRegistrationModal
          target={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => { setCancelTarget(null); load(); }}
        />
      )}
      {changeCategoryTarget && (
        <ChangeCategoryModal
          registration={changeCategoryTarget.registration}
          currentActivity={changeCategoryTarget.currentActivity}
          activities={activities}
          onClose={() => setChangeCategoryTarget(null)}
          onChanged={() => { setChangeCategoryTarget(null); load(); }}
        />
      )}
      {editTarget && (
        <EditActivityModal
          eventId={event.id}
          activity={editTarget}
          eventStart={event.start_date}
          eventEnd={event.end_date}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
      {rescheduleTarget && (
        <RescheduleActivityModal
          eventId={event.id}
          event={event}
          activity={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onDone={() => { setRescheduleTarget(null); load(); }}
        />
      )}
      {editEventOpen && (
        <EditEventModal
          event={event}
          onClose={() => setEditEventOpen(false)}
          onSaved={() => { setEditEventOpen(false); load(); }}
        />
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3 text-center">
    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-lg font-bold mt-0.5">{value}</p>
  </div>
);

const ActivitySummaryCard = ({ activity, onCancel, onAddRegistration, onEdit, onReschedule, disabled }) => {
  const filled = activity.current_registrations || 0;
  const max = activity.max_participants || 0;
  const pct = max ? Math.min(100, Math.round((filled / max) * 100)) : 0;
  const revenue = filled * (activity.price_per_unit || 0);
  const isFree = (activity.price_per_unit || 0) === 0;
  return (
    <div className="bg-white border rounded-2xl p-4" data-testid={`activity-card-${activity.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm">{activity.title}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusPill(activity.status)}`}>{activity.status}</span>
            <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase">{activity.activity_type}</span>
            <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase">{activity.format}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {fmtDate(activity.date)} · {fmtTime(activity.start_time)}–{fmtTime(activity.end_time)} · {activity.sport !== 'none' ? activity.sport : '—'}
          </p>
          {activity.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2 italic">{activity.description}</p>
          )}
        </div>
        <div className="flex gap-1">
          {!disabled && !['cancelled', 'completed'].includes(activity.status) && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit} data-testid={`edit-activity-${activity.id}`}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
          {!disabled && onReschedule && !['cancelled', 'completed'].includes(activity.status) && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={onReschedule} data-testid={`reschedule-activity-${activity.id}`}>
              <CalendarIcon className="h-3 w-3 mr-1" /> Reschedule
            </Button>
          )}
          {!disabled && activity.status === 'open' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddRegistration} data-testid={`add-reg-${activity.id}`}>
              <UserPlus className="h-3 w-3 mr-1" /> Add
            </Button>
          )}
          {!disabled && !['cancelled', 'completed'].includes(activity.status) && (
            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={onCancel} data-testid={`cancel-activity-${activity.id}`}>
              <XCircle className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-gray-500 text-[10px] uppercase">Filled</p>
          <p className="font-semibold">{filled} / {max}</p>
          <div className="mt-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-gray-900" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div>
          <p className="text-gray-500 text-[10px] uppercase">Price</p>
          <p className="font-semibold">{isFree ? 'Free' : `₹${activity.price_per_unit}${activity.format === 'team' ? '/team' : ''}`}</p>
        </div>
        <div>
          <p className="text-gray-500 text-[10px] uppercase">Revenue</p>
          <p className="font-semibold">₹{revenue.toFixed(0)}</p>
        </div>
      </div>
    </div>
  );
};

const RescheduleActivityModal = ({ eventId, event, activity, onClose, onDone }) => {
  const [step, setStep] = useState('date'); // date | refund | review | result
  const [form, setForm] = useState({
    new_date: activity.date || '',
    new_start_time: activity.start_time || '',
    new_end_time: activity.end_time || '',
  });
  const [preview, setPreview] = useState(null);
  const [decision, setDecision] = useState('keep'); // keep | refund_all
  const [message, setMessage] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    setBusy(true); setErr('');
    try {
      const res = await api.request(`/events/${eventId}/activities/${activity.id}/reschedule/preview`, {
        method: 'POST',
        body: JSON.stringify({
          new_date: form.new_date,
          new_start_time: form.new_start_time || null,
          new_end_time: form.new_end_time || null,
        }),
      });
      setPreview(res);
      setStep('refund');
    } catch (e) {
      setErr(e.message || 'Could not validate the new date');
    } finally { setBusy(false); }
  };

  const chooseDecision = (d) => {
    setDecision(d);
    setMessage(d === 'refund_all' ? (preview?.message_refund || '') : (preview?.message_keep || ''));
    setStep('review');
  };

  const commit = async () => {
    setBusy(true); setErr('');
    try {
      const res = await api.request(`/events/${eventId}/activities/${activity.id}/reschedule`, {
        method: 'POST',
        body: JSON.stringify({
          new_date: form.new_date,
          new_start_time: form.new_start_time || null,
          new_end_time: form.new_end_time || null,
          refund_decision: decision,
          message,
          notify,
        }),
      });
      setResult(res);
      setStep('result');
    } catch (e) {
      setErr(e.message || 'Reschedule failed');
    } finally { setBusy(false); }
  };

  const paidCount = preview?.counts?.paid ?? 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="reschedule-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><CalendarIcon className="h-4 w-4" /> Reschedule “{activity.title}”</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-700 flex items-start gap-2" data-testid="reschedule-error">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /><p>{err}</p>
          </div>
        )}

        {/* Step 1: new date/time */}
        {step === 'date' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Currently {fmtDate(activity.date)} · {fmtTime(activity.start_time)}–{fmtTime(activity.end_time)}</p>
            <div>
              <label className="text-xs font-medium text-gray-600">New date</label>
              <input type="date" value={form.new_date} onChange={e => setForm(p => ({ ...p, new_date: e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="reschedule-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Start time</label>
                <input type="time" value={form.new_start_time} onChange={e => setForm(p => ({ ...p, new_start_time: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="reschedule-start" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">End time</label>
                <input type="time" value={form.new_end_time} onChange={e => setForm(p => ({ ...p, new_end_time: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="reschedule-end" />
              </div>
            </div>
            <Button onClick={doPreview} disabled={busy || !form.new_date} className="w-full" data-testid="reschedule-continue">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Continue
            </Button>
          </div>
        )}

        {/* Step 2: refund decision */}
        {step === 'refund' && preview && (
          <div className="space-y-3">
            {preview.warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="reschedule-warning">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /><p>{preview.warning_reason}</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p><span className="text-gray-500">New date:</span> <strong>{preview.new_when}</strong></p>
              <p className="mt-1 text-xs text-gray-500">{preview.counts.active} registered · {preview.counts.paid} paid · {preview.counts.free} free</p>
            </div>
            <p className="text-sm font-medium">Refund already-registered members?</p>
            <button onClick={() => chooseDecision('refund_all')} className="w-full text-left border rounded-lg p-3 hover:border-blue-400" data-testid="reschedule-refund-all">
              <p className="text-sm font-semibold">Refund all paid registrants ({paidCount})</p>
              <p className="text-xs text-gray-500 mt-0.5">Refunds via Razorpay and frees their spot. Free registrants are kept and notified.</p>
            </button>
            <button onClick={() => chooseDecision('keep')} className="w-full text-left border rounded-lg p-3 hover:border-blue-400" data-testid="reschedule-keep">
              <p className="text-sm font-semibold">No refund — keep registrations</p>
              <p className="text-xs text-gray-500 mt-0.5">Everyone stays registered on the new date and is notified.</p>
            </button>
          </div>
        )}

        {/* Step 3: review notification */}
        {step === 'review' && (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs text-blue-800">
              {decision === 'refund_all'
                ? `${paidCount} paid registrant(s) will be refunded via Razorpay and their spots freed. Free registrants are kept.`
                : 'All registrants keep their spot on the new date.'}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Message to registrants (review before sending)</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="reschedule-message" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} data-testid="reschedule-notify" />
              Email this message to registrants now
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('refund')} className="flex-1">Back</Button>
              <Button onClick={commit} disabled={busy || !message.trim()} className="flex-1" data-testid="reschedule-confirm">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm reschedule
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: result */}
        {step === 'result' && result && (
          <div className="space-y-3" data-testid="reschedule-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Rescheduled to {fmtDate(result.new_date)}</p>
                <p className="text-xs mt-0.5">
                  {result.refund_decision === 'refund_all'
                    ? `${result.refunded} refunded, ${result.failed} failed.` : 'No refunds issued.'}
                  {' '}{result.notified} member(s) notified{result.notify_failed ? `, ${result.notify_failed} email(s) failed` : ''}.
                </p>
              </div>
            </div>
            {result.failures?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="reschedule-failures">
                <p className="font-semibold mb-1">Refund failed for {result.failures.length} member(s) — retry/handle manually:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {result.failures.map((f, i) => <li key={i}>{f.name}: {f.error}</li>)}
                </ul>
              </div>
            )}
            <Button onClick={onDone} className="w-full" data-testid="reschedule-done">Done</Button>
          </div>
        )}
      </div>
    </div>
  );
};


const RegistrationRow = ({ reg, activity, expanded, onToggle, onCancel, onChangeCategory }) => {
  const isTeam = reg.registration_type === 'team';
  const isActive = reg.status === 'active';
  const isPending = reg.payment_status === 'pending';
  const [sendingLink, setSendingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState(reg.payment_link_short_url || '');

  // Generate / reuse a Razorpay payment link for an unpaid registration.
  // The webhook (payments.py — payment_link.paid handler) auto-reconciles
  // the registration's payment_status when the customer pays the link.
  const sendPaymentLink = async () => {
    setSendingLink(true);
    try {
      const res = await api.request(`/events/admin/registrations/${reg.id}/send-payment-link`, { method: 'POST' });
      setPaymentLink(res.short_url);
      // Copy to clipboard so admin can paste into WhatsApp/SMS immediately.
      try {
        await navigator.clipboard?.writeText(res.short_url);
        toast({
          title: res.reused ? 'Existing link copied' : 'Payment link generated',
          description: `${res.short_url} — copied to clipboard. Share with the captain via WhatsApp/SMS.`,
        });
      } catch {
        toast({
          title: res.reused ? 'Existing payment link' : 'Payment link generated',
          description: res.short_url,
        });
      }
    } catch (e) {
      toast({ title: 'Could not generate link', description: e.message, variant: 'destructive' });
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <div className="border rounded-xl bg-white" data-testid={`reg-row-${reg.id}`}>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium">
              {isTeam ? `🏆 ${reg.team_name || 'Team'}` : reg.name || 'Player'}
            </p>
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{activity?.title}</span>
            {!isActive && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded uppercase">{reg.status}</span>}
            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
              reg.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
              reg.payment_status === 'manual' ? 'bg-blue-100 text-blue-700' :
              reg.payment_status === 'refunded' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-700'
            }`}>{reg.payment_status}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {isTeam
              ? `${(reg.players || []).length} players · captain ${reg.captain_phone || ''}`
              : `${reg.phone || ''}${reg.email ? ' · ' + reg.email : ''}`}
            {reg.amount_paid > 0 && ` · ₹${reg.amount_paid}`}
          </p>
          {paymentLink && (
            <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3" />
              <a href={paymentLink} target="_blank" rel="noreferrer" className="underline truncate" data-testid={`payment-link-${reg.id}`}>
                {paymentLink}
              </a>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isActive && isPending && (
            <button
              onClick={sendPaymentLink}
              disabled={sendingLink}
              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"
              title={paymentLink ? "Regenerate / copy payment link" : "Generate Razorpay payment link"}
              data-testid={`send-payment-link-${reg.id}`}
            >
              {sendingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          )}
          {isTeam && (
            <button onClick={onToggle} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`reg-expand-${reg.id}`}>
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
          {isActive && (
            <button onClick={onChangeCategory} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded" title="Change category" data-testid={`reg-change-category-${reg.id}`}>
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          )}
          {isActive && (
            <button onClick={onCancel} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Cancel & refund" data-testid={`reg-cancel-${reg.id}`}>
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {expanded && isTeam && (
        <div className="px-3 pb-3 pt-1 border-t bg-gray-50 rounded-b-xl">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Roster</p>
          <ul className="text-xs space-y-1">
            {(reg.players || []).map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span><strong>{i + 1}. {p.name}</strong> · {p.phone}{p.position ? ` · ${p.position}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ChangeCategoryModal = ({ registration, currentActivity, activities, onClose, onChanged }) => {
  const [targetId, setTargetId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [reconciliation, setReconciliation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const availableTargets = (activities || []).filter(a =>
    a.id !== currentActivity?.id && !['cancelled', 'completed'].includes(a.status)
  );

  const runPreview = async (newId) => {
    setTargetId(newId);
    setPreview(null);
    setPreviewError('');
    setReconciliation('');
    if (!newId) return;
    setPreviewing(true);
    try {
      const res = await api.request(`/events/registrations/${registration.id}/change-category`, {
        method: 'POST',
        body: JSON.stringify({ new_activity_id: newId }),
      });
      setPreview(res);
    } catch (e) {
      setPreviewError(e.message || 'Could not load preview');
    } finally {
      setPreviewing(false);
    }
  };

  const canSubmit = preview && preview.capacity_ok && preview.format_transition !== 'block' && !!reconciliation && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.request(`/events/registrations/${registration.id}/change-category`, {
        method: 'POST',
        body: JSON.stringify({ new_activity_id: targetId, reconciliation }),
      });
      toast({ title: 'Category changed', description: 'Registration moved successfully.' });
      onChanged?.();
    } catch (e) {
      toast({ title: 'Move failed', description: e.message || 'Please retry.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const priceDiffLabel = preview ? (
    preview.price_diff === 0 ? 'No price change' :
    preview.price_diff > 0 ? `Customer would owe \u20b9${preview.price_diff} more` :
    `Refund of \u20b9${Math.abs(preview.price_diff)} owed to customer`
  ) : '';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="change-category-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h2 className="font-semibold text-sm">Change category</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="change-category-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="text-xs text-gray-600">
            Moving <span className="font-medium text-gray-900">
              {registration.registration_type === 'team' ? (registration.team_name || 'Team') : (registration.name || 'Player')}
            </span> from <span className="font-medium text-gray-900">{currentActivity?.title}</span>.
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Move to category</label>
            <select
              value={targetId}
              onChange={e => runPreview(e.target.value)}
              className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="change-category-target-select"
            >
              <option value="">Select a category...</option>
              {availableTargets.map(a => (
                <option key={a.id} value={a.id}>
                  {a.title} \u00b7 {a.format} \u00b7 \u20b9{a.price_per_unit || 0} \u00b7 {a.date}
                </option>
              ))}
            </select>
          </div>

          {previewing && (
            <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading preview...</div>
          )}
          {previewError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{previewError}</div>
          )}

          {preview && (
            <div className="space-y-3" data-testid="change-category-preview">
              {preview.format_transition === 'block' && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="change-category-format-block">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{preview.format_message}</span>
                </div>
              )}
              {preview.format_transition === 'warn' && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="change-category-format-warn">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{preview.format_message}</span>
                </div>
              )}
              {preview.target_full && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="change-category-full">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Target category is full \u2014 pick a different one.</span>
                </div>
              )}

              <div className="border rounded-lg p-3 bg-gray-50 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-gray-600">Old price</span><span>\u20b9{preview.old_price}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">New price</span><span>\u20b9{preview.new_price}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Amount already paid</span><span>\u20b9{preview.old_amount_paid}</span></div>
                <div className="flex justify-between font-medium pt-1 border-t mt-1">
                  <span>Difference</span>
                  <span className={preview.price_diff === 0 ? '' : preview.price_diff > 0 ? 'text-amber-700' : 'text-red-700'}>
                    {priceDiffLabel}
                  </span>
                </div>
                {preview.refund_owed_full && (
                  <div className="text-red-700 pt-1 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>Target is a <strong>free</strong> category \u2014 the full \u20b9{preview.old_amount_paid} paid earlier is refundable.</span>
                  </div>
                )}
              </div>

              {(preview.schedule_change.date_changed || preview.schedule_change.time_changed) && (
                <div className="border rounded-lg p-3 text-xs">
                  <p className="font-semibold text-gray-700 mb-1">Schedule change</p>
                  <p className="text-gray-600">
                    {preview.schedule_change.old_date} {preview.schedule_change.old_start}\u2013{preview.schedule_change.old_end}
                    <br />\u2192 <span className="text-gray-900 font-medium">{preview.schedule_change.new_date} {preview.schedule_change.new_start}\u2013{preview.schedule_change.new_end}</span>
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-600">
                Target capacity: {preview.target_activity.current_registrations}/{preview.target_activity.max_participants}
                {preview.capacity_ok ? <span className="text-emerald-700 ml-1">has spots</span> : <span className="text-red-700 ml-1">full</span>}
              </div>

              {preview.format_transition !== 'block' && preview.capacity_ok && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">How to handle the money?</label>
                  <p className="text-[11px] text-gray-500 mt-0.5 mb-2">
                    The amount already paid will NOT be changed automatically. Pick a note for the audit log:
                  </p>
                  <div className="space-y-1.5">
                    <ReconOption value="leave_as_is" checked={reconciliation === 'leave_as_is'} onChange={setReconciliation}
                                 label="Leave payment as is" hint="Old amount stays on the registration. No refund or top-up owed." />
                    <ReconOption value="note_refund_owed" checked={reconciliation === 'note_refund_owed'} onChange={setReconciliation}
                                 label="Note: refund owed to customer" hint="Handle the refund separately via Razorpay. This just logs the intent." disabled={preview.price_diff >= 0} />
                    <ReconOption value="note_topup_owed" checked={reconciliation === 'note_topup_owed'} onChange={setReconciliation}
                                 label="Note: top-up owed by customer" hint="Send a payment link separately. This just logs the intent." disabled={preview.price_diff <= 0} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="change-category-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} className="flex-1" data-testid="change-category-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm move'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ReconOption = ({ value, checked, onChange, label, hint, disabled }) => (
  <label className={`flex items-start gap-2 p-2 border rounded-lg text-xs cursor-pointer ${
    checked ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-white'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input
      type="radio"
      name="reconciliation"
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={() => onChange(value)}
      className="mt-0.5"
      data-testid={`recon-${value}`}
    />
    <span>
      <span className="font-medium text-gray-900">{label}</span>
      <span className="block text-[11px] text-gray-500 mt-0.5">{hint}</span>
    </span>
  </label>
);


const AdminAddRegistrationModal = ({ activity, eventId, onClose, onAdded }) => {
  const isTeam = activity.format === 'team';
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState(
    isTeam ? Array.from({ length: activity.team_size || 1 }, () => ({ name: '', phone: '', position: '' })) : []
  );
  const [saving, setSaving] = useState(false);

  const setPlayer = (i, patch) => setPlayers(arr => arr.map((p, idx) => idx === i ? { ...p, ...patch } : p));

  const submit = async () => {
    setSaving(true);
    try {
      const body = isTeam
        ? { team_name: teamName, email, players: players.map(p => ({ ...p, phone: (p.phone || '').replace(/\D/g, '') })) }
        : { name, phone: phone.replace(/\D/g, ''), email };
      await api.request(`/events/${eventId}/activities/${activity.id}/add-registration`, {
        method: 'POST', body: JSON.stringify(body),
      });
      toast({ title: 'Added' });
      onAdded();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="admin-add-reg-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Add Registration · {activity.title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Manual offline registration. No payment is collected.</p>
        {!isTeam ? (
          <div className="space-y-3">
            <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Name" value={name} onChange={e => setName(e.target.value)} data-testid="admin-add-name" />
            <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} data-testid="admin-add-phone" />
            <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-add-email" />
          </div>
        ) : (
          <div className="space-y-3">
            <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Team name" value={teamName} onChange={e => setTeamName(e.target.value)} data-testid="admin-add-team-name" />
            <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Captain email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
            <p className="text-xs font-semibold text-gray-700">Players (exactly {activity.team_size})</p>
            {players.map((p, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <input className="border rounded-lg px-2 py-1.5 text-sm col-span-2" placeholder={`Player ${i + 1}`} value={p.name} onChange={e => setPlayer(i, { name: e.target.value })} data-testid={`admin-add-player-${i}-name`} />
                <input className="border rounded-lg px-2 py-1.5 text-sm" placeholder="Phone" value={p.phone} onChange={e => setPlayer(i, { phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid={`admin-add-player-${i}-phone`} />
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1" data-testid="admin-add-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const EditEventModal = ({ event, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: event.title || '',
    description: event.description || '',
    terms_and_conditions: event.terms_and_conditions || '',
    refund_policy: event.refund_policy || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Event title cannot be empty', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.request(`/events/${event.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description,
          terms_and_conditions: form.terms_and_conditions,
          refund_policy: form.refund_policy,
        }),
      });
      toast({ title: 'Event updated' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Failed to update event', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="edit-event-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold">Edit Event</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black" data-testid="edit-event-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Event title *</label>
            <input
              value={form.title}
              onChange={e => set({ title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-event-title"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Event description</label>
            <textarea
              value={form.description}
              onChange={e => set({ description: e.target.value })}
              rows={4}
              placeholder="Describe your event — dates, highlights, prizes, etc."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-event-description"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Terms &amp; Conditions</label>
            <textarea
              value={form.terms_and_conditions}
              onChange={e => set({ terms_and_conditions: e.target.value })}
              rows={5}
              placeholder="Rules, eligibility, gear requirements, code of conduct, etc."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-event-terms"
            />
            <p className="text-[10px] text-gray-400 mt-1">Shown as a collapsible section on the registration modal.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Cancellation &amp; Refund Policy</label>
            <textarea
              value={form.refund_policy}
              onChange={e => set({ refund_policy: e.target.value })}
              rows={5}
              placeholder="Cancellation window, refund percentages, non-refundable fees, etc."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-event-refund-policy"
            />
            <p className="text-[10px] text-gray-400 mt-1">Shown as a collapsible section on the registration modal.</p>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-event-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-event-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-1" /> Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};


const EditActivityModal = ({ eventId, activity, eventStart, eventEnd, onClose, onSaved }) => {
  const hasRegs = (activity.current_registrations || 0) > 0;
  const [form, setForm] = useState({
    title: activity.title || '',
    description: activity.description || '',
    activity_type: activity.activity_type || 'tournament',
    sport: activity.sport || 'football',
    format: activity.format || 'individual',
    date: activity.date || '',
    start_time: activity.start_time || '',
    end_time: activity.end_time || '',
    max_participants: activity.max_participants || 1,
    price_per_unit: activity.price_per_unit || 0,
    registration_deadline: activity.registration_deadline || '',
    team_size: activity.team_size || 1,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const isTeam = form.format === 'team';
  const isFree = form.format === 'free';
  const update = (patch) => setForm(p => ({ ...p, ...patch }));

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        title: form.title,
        description: form.description,
        activity_type: form.activity_type,
        sport: form.sport,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        max_participants: Number(form.max_participants),
        price_per_unit: Number(form.price_per_unit) || 0,
        registration_deadline: form.registration_deadline || null,
      };
      // Only allow format/team_size change when no registrations
      if (!hasRegs) {
        body.format = form.format;
        if (form.format === 'team') body.team_size = Number(form.team_size);
      }
      await api.request(`/events/${eventId}/activities/${activity.id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      toast({ title: 'Activity updated' });
      onSaved();
    } catch (e) {
      setErr(e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="edit-activity-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Edit Activity</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {hasRegs && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <p>This activity has registrations. You cannot change the registration format. Capacity cannot be reduced below {activity.current_registrations}.</p>
          </div>
        )}

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-700" data-testid="edit-activity-error">{err}</div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-700">Activity Name</label>
            <input type="text" value={form.title}
              onChange={e => update({ title: e.target.value })}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-activity-title" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Description</label>
            <textarea value={form.description}
              onChange={e => update({ description: e.target.value })}
              rows={3}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm resize-y"
              data-testid="edit-activity-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Type</label>
              <select value={form.activity_type} onChange={e => update({ activity_type: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize" data-testid="edit-activity-type">
                {ACTIVITY_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Sport</label>
              <select value={form.sport} onChange={e => update({ sport: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize" data-testid="edit-activity-sport">
                {SPORTS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Format</label>
              <select value={form.format}
                disabled={hasRegs}
                onChange={e => {
                  const f = e.target.value;
                  update({ format: f, ...(f === 'free' ? { price_per_unit: 0 } : {}) });
                }}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="edit-activity-format">
                {FORMATS.map(f => <option key={f} value={f} className="capitalize">{f}</option>)}
              </select>
            </div>
            {isTeam && (
              <div>
                <label className="text-xs font-semibold text-gray-700">Players Per Team</label>
                <input type="number" min="1" value={form.team_size}
                  disabled={hasRegs}
                  onChange={e => update({ team_size: Number(e.target.value) })}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  data-testid="edit-activity-team-size" />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              {isTeam ? 'Maximum Teams' : 'Maximum Participants'}
            </label>
            <input type="number" min={Math.max(1, activity.current_registrations || 1)} value={form.max_participants}
              onChange={e => update({ max_participants: Number(e.target.value) })}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-activity-max" />
            {hasRegs && (
              <p className="text-[10px] text-gray-500 mt-1">Minimum: {activity.current_registrations} (current registrations)</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Date</label>
              <input type="date" value={form.date}
                min={eventStart} max={eventEnd}
                onChange={e => update({ date: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-activity-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Start</label>
              <input type="time" value={form.start_time}
                onChange={e => update({ start_time: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-activity-start" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">End</label>
              <input type="time" value={form.end_time}
                onChange={e => update({ end_time: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-activity-end" />
            </div>
          </div>

          {!isFree && (
            <div>
              <label className="text-xs font-semibold text-gray-700">
                {isTeam ? 'Price per Team (₹)' : 'Price per Person (₹)'}
              </label>
              <input type="number" min="0" value={form.price_per_unit}
                onChange={e => update({ price_per_unit: Number(e.target.value) })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-activity-price" />
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-700">Registration Deadline (optional)</label>
            <input type="datetime-local" value={(form.registration_deadline || '').slice(0, 16)}
              onChange={e => update({ registration_deadline: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-activity-deadline" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1" data-testid="edit-activity-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CouponsSection = ({ eventId, activities }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [usesTarget, setUsesTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.request(`/events/${eventId}/coupons`);
      setCoupons(data || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? This cannot be undone.`)) return;
    try {
      await api.request(`/events/${eventId}/coupons/${c.id}`, { method: 'DELETE' });
      toast({ title: 'Coupon deleted' });
      load();
    } catch (err) {
      toast({ title: 'Cannot delete', description: err.message, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (c) => {
    try {
      await api.request(`/events/${eventId}/coupons/${c.id}`, {
        method: 'PATCH', body: JSON.stringify({ is_active: !c.is_active }),
      });
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/events/${eventId}/coupons/export`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Export failed');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fileName = match ? match[1] : 'coupon_uses.xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const expiredAt = (c) => {
    if (!c.valid_until) return null;
    try {
      const d = new Date(c.valid_until);
      return d.getTime() < Date.now() ? d : null;
    } catch { return null; }
  };
  const fmtScope = (c) => {
    if (!c.activity_ids || c.activity_ids.length === 0) return 'All activities';
    return c.activity_ids
      .map(id => activities.find(a => a.id === id)?.title || id.slice(0, 6))
      .join(', ');
  };

  return (
    <div className="bg-white border rounded-2xl p-4 mb-6" data-testid="coupons-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-semibold text-sm flex items-center gap-1.5">
          <Tag className="h-4 w-4" /> Coupons
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleExport} disabled={downloading} data-testid="coupon-export-btn">
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            Export
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-coupon-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Create Coupon
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-gray-400 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></p>
      ) : coupons.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6">No coupons yet. Create one to offer discounts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 uppercase tracking-wide text-[10px] border-b">
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Discount</th>
                <th className="py-2 pr-3">Scope</th>
                <th className="py-2 pr-3">Uses</th>
                <th className="py-2 pr-3">Valid Until</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => {
                const expired = expiredAt(c);
                const status = expired ? 'Expired' : (c.is_active ? 'Active' : 'Inactive');
                const usesLabel = c.max_uses ? `${c.usage_count || 0} / ${c.max_uses}` : `${c.usage_count || 0} / ∞`;
                return (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`coupon-row-${c.code}`}>
                    <td className="py-2 pr-3 font-mono font-bold">{c.code}</td>
                    <td className="py-2 pr-3">
                      {c.discount_type === 'percentage'
                        ? `${c.discount_value}% off`
                        : `₹${c.discount_value} off`}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 max-w-[200px] truncate" title={fmtScope(c)}>{fmtScope(c)}</td>
                    <td className="py-2 pr-3">{usesLabel}</td>
                    <td className="py-2 pr-3 text-gray-500">{c.valid_until ? new Date(c.valid_until).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        status === 'Active' ? 'bg-emerald-100 text-emerald-700'
                        : status === 'Expired' ? 'bg-gray-100 text-gray-600'
                        : 'bg-amber-100 text-amber-800'}`}
                      >{status}</span>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <div className="inline-flex gap-1">
                        <button title="View usage" onClick={() => setUsesTarget(c)} className="p-1 hover:bg-gray-100 rounded" data-testid={`coupon-uses-${c.code}`}>
                          <Eye className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                        <button title="Edit" onClick={() => setEditTarget(c)} className="p-1 hover:bg-gray-100 rounded" data-testid={`coupon-edit-${c.code}`}>
                          <Pencil className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                        <button
                          title={c.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggleActive(c)}
                          className="p-1 hover:bg-gray-100 rounded"
                          data-testid={`coupon-toggle-${c.code}`}
                        >
                          {c.is_active
                            ? <EyeOff className="h-3.5 w-3.5 text-amber-600" />
                            : <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />}
                        </button>
                        {(c.usage_count || 0) === 0 && (
                          <button title="Delete" onClick={() => handleDelete(c)} className="p-1 hover:bg-red-50 rounded" data-testid={`coupon-delete-${c.code}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CouponEditorModal
          eventId={eventId}
          activities={activities}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); load(); }}
        />
      )}
      {editTarget && (
        <CouponEditorModal
          eventId={eventId}
          activities={activities}
          editing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
      {usesTarget && (
        <CouponUsesModal
          eventId={eventId}
          coupon={usesTarget}
          activities={activities}
          onClose={() => setUsesTarget(null)}
        />
      )}
    </div>
  );
};

const CouponEditorModal = ({ eventId, activities, editing, onClose, onSaved }) => {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    code: editing?.code || '',
    discount_type: editing?.discount_type || 'percentage',
    discount_value: editing?.discount_value ?? 10,
    scope_all: !editing || !(editing.activity_ids && editing.activity_ids.length),
    activity_ids: editing?.activity_ids || [],
    min_order_amount: editing?.min_order_amount ?? '',
    max_uses: editing?.max_uses ?? '',
    max_uses_per_customer: editing?.max_uses_per_customer ?? '',
    valid_from: editing?.valid_from ? editing.valid_from.slice(0, 16) : '',
    valid_until: editing?.valid_until ? editing.valid_until.slice(0, 16) : '',
    is_active: editing?.is_active !== false,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const update = (patch) => setForm(p => ({ ...p, ...patch }));
  const toggleAct = (id) => {
    setForm(p => ({
      ...p,
      activity_ids: p.activity_ids.includes(id)
        ? p.activity_ids.filter(x => x !== id)
        : [...p.activity_ids, id],
    }));
  };
  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        activity_ids: form.scope_all ? [] : form.activity_ids,
        min_order_amount: form.min_order_amount === '' ? null : Number(form.min_order_amount),
        max_uses: form.max_uses === '' ? null : Number(form.max_uses),
        max_uses_per_customer: form.max_uses_per_customer === '' ? null : Number(form.max_uses_per_customer),
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        is_active: !!form.is_active,
      };
      if (!isEdit) body.code = form.code.trim().toUpperCase();
      const path = isEdit
        ? `/events/${eventId}/coupons/${editing.id}`
        : `/events/${eventId}/coupons`;
      const method = isEdit ? 'PATCH' : 'POST';
      await api.request(path, { method, body: JSON.stringify(body) });
      toast({ title: isEdit ? 'Coupon updated' : 'Coupon created' });
      onSaved();
    } catch (e) {
      setErr(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="coupon-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{isEdit ? `Edit ${editing.code}` : 'Create Coupon'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 text-xs text-red-700" data-testid="coupon-form-error">{err}</div>
        )}

        <div className="space-y-3">
          {!isEdit && (
            <div>
              <label className="text-xs font-semibold text-gray-700">Coupon Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={e => update({ code: e.target.value.toUpperCase() })}
                placeholder="EARLY20"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm uppercase font-mono"
                data-testid="coupon-code-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">Code cannot be changed once created.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Discount Type *</label>
              <select value={form.discount_type} onChange={e => update({ discount_type: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="coupon-type">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                {form.discount_type === 'percentage' ? 'Discount % *' : 'Discount ₹ *'}
              </label>
              <input
                type="number" min="0"
                max={form.discount_type === 'percentage' ? 100 : undefined}
                value={form.discount_value}
                onChange={e => update({ discount_value: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-value"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Applies to</label>
            <div className="mt-1 flex items-center gap-3">
              <label className="text-xs flex items-center gap-1.5">
                <input type="radio" checked={form.scope_all} onChange={() => update({ scope_all: true })} data-testid="coupon-scope-all" />
                All activities
              </label>
              <label className="text-xs flex items-center gap-1.5">
                <input type="radio" checked={!form.scope_all} onChange={() => update({ scope_all: false })} data-testid="coupon-scope-specific" />
                Select specific
              </label>
            </div>
            {!form.scope_all && (
              <div className="mt-2 border rounded-lg p-2 space-y-1 max-h-32 overflow-y-auto" data-testid="coupon-activity-list">
                {activities.map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={form.activity_ids.includes(a.id)} onChange={() => toggleAct(a.id)} />
                    <span>{a.title}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Min order ₹ (optional)</label>
              <input type="number" min="0" value={form.min_order_amount} onChange={e => update({ min_order_amount: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-min-order" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Max total uses</label>
              <input type="number" min="1" value={form.max_uses} onChange={e => update({ max_uses: e.target.value })}
                placeholder="Unlimited"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-max-uses" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Max uses per customer</label>
            <input type="number" min="1" value={form.max_uses_per_customer} onChange={e => update({ max_uses_per_customer: e.target.value })}
              placeholder="Unlimited"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-max-per-customer" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Valid from (optional)</label>
              <input type="datetime-local" value={form.valid_from} onChange={e => update({ valid_from: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-valid-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Valid until (optional)</label>
              <input type="datetime-local" value={form.valid_until} onChange={e => update({ valid_until: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" data-testid="coupon-valid-until" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_active} onChange={e => update({ is_active: e.target.checked })} data-testid="coupon-active-toggle" />
            <span className="font-semibold text-gray-700">Active</span>
          </label>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving || (!isEdit && !form.code.trim())} className="flex-1" data-testid="coupon-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? 'Save Changes' : 'Create')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CouponUsesModal = ({ eventId, coupon, activities, onClose }) => {
  const [uses, setUses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const data = await api.request(`/events/${eventId}/coupons/${coupon.id}/uses`);
        setUses(data || []);
      } catch (e) {
        toast({ title: 'Error', description: e.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, coupon.id]);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="coupon-uses-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Coupon usage — <span className="font-mono">{coupon.code}</span></h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        {loading ? (
          <p className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-gray-400" /></p>
        ) : uses.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No uses yet</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-gray-500 border-b">
                <th className="py-1.5 pr-2">Phone</th>
                <th className="py-1.5 pr-2">Activity</th>
                <th className="py-1.5 pr-2">Original</th>
                <th className="py-1.5 pr-2">Discount</th>
                <th className="py-1.5 pr-2">Paid</th>
                <th className="py-1.5 pr-2">Used At</th>
              </tr>
            </thead>
            <tbody>
              {uses.map(u => {
                const act = activities.find(a => a.id === u.activity_id);
                return (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 font-mono">{u.customer_phone}</td>
                    <td className="py-1.5 pr-2 truncate max-w-[120px]" title={act?.title}>{act?.title || u.activity_id?.slice(0, 8)}</td>
                    <td className="py-1.5 pr-2">₹{Number(u.original_amount).toLocaleString('en-IN')}</td>
                    <td className="py-1.5 pr-2 text-emerald-700">−₹{Number(u.discount_amount).toLocaleString('en-IN')}</td>
                    <td className="py-1.5 pr-2 font-semibold">₹{Number(u.final_amount).toLocaleString('en-IN')}</td>
                    <td className="py-1.5 pr-2 text-gray-500">{new Date(u.used_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const CancelRegistrationModal = ({ target, onClose, onCancelled }) => {
  const [cancelling, setCancelling] = useState(false);
  const reg = target.registration;
  const refundAmount = reg.amount_paid || 0;
  const submit = async () => {
    setCancelling(true);
    try {
      const res = await api.request(`/events/registrations/${reg.id}/cancel-admin`, { method: 'POST' });
      toast({
        title: 'Cancelled',
        description: res.refunded ? `Refund of ₹${refundAmount.toFixed(0)} initiated.` : 'Registration cancelled. No payment to refund.',
      });
      onCancelled();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" data-testid="cancel-reg-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5">
        <h3 className="font-semibold mb-2">Cancel Registration?</h3>
        <p className="text-sm text-gray-600 mb-3">
          {reg.registration_type === 'team' ? <>Team <strong>{reg.team_name}</strong></> : <strong>{reg.name}</strong>}
          {' · '} {target.activity?.title}
        </p>
        <div className={`rounded-xl p-3 mb-4 ${refundAmount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-500 uppercase">Refund amount</p>
          <p className="text-lg font-bold mt-0.5">{refundAmount > 0 ? `₹${refundAmount.toFixed(0)}` : 'No refund (offline / unpaid)'}</p>
          {refundAmount > 0 && (
            <p className="text-xs text-gray-600 mt-1">Refund typically arrives in 5–7 business days.</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Keep</Button>
          <Button onClick={submit} disabled={cancelling} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="cancel-reg-confirm">
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel & Refund'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== ROOT ==============
const EventsRoot = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.request('/events');
      setEvents(data || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <EventsList
      events={events}
      loading={loading}
      onCreate={() => navigate('/admin/events/new')}
      onOpen={(id) => navigate(`/admin/events/${id}`)}
    />
  );
};

const CreateRoot = () => {
  const navigate = useNavigate();
  return (
    <CreateEventWizard
      onCancel={() => navigate('/admin/events')}
      onCreated={(id) => navigate(`/admin/events/${id}`)}
    />
  );
};

const DetailRoot = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  return <EventDetail eventId={id} onBack={() => navigate('/admin/events')} />;
};

const EventsManagement = () => (
  <Routes>
    <Route path="/" element={<EventsRoot />} />
    <Route path="/new" element={<CreateRoot />} />
    <Route path="/:id" element={<DetailRoot />} />
  </Routes>
);

export default EventsManagement;
